<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useCustomAttributeValueValidation } from 'dashboard/composables/useCustomAttributeValueValidation';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();
const store = useStore();

const companiesStore = useCompaniesStore();
const uiFlags = computed(() => companiesStore.getUIFlags);
const isImporting = computed(() => uiFlags.value.isImporting);

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const companyAttributes = useMapGetter('attributes/getCompanyAttributes');
const dealAttributes = useMapGetter('attributes/getDealAttributes');

onMounted(() => {
  if (
    !contactAttributes.value.length &&
    !companyAttributes.value.length &&
    !dealAttributes.value.length
  ) {
    store.dispatch('attributes/get');
  }
});

const CUSTOM_ATTRIBUTE_PREFIX = 'custom_attributes.';
const CONTACT_PREFIX = 'contact.';
const DEAL_PREFIX = 'deal.';

const dialogRef = ref(null);
const fileInput = ref(null);

const selectedFile = ref(null);
const selectedFileName = ref('');
const csvHeaders = ref([]);
const csvPreviewRows = ref([]);
const fieldMapping = ref({});

const CRM_FIELDS = ['name', 'domain', 'description'];
const CONTACT_FIELDS = ['name', 'email', 'phone_number', 'identifier', 'city'];
const DEAL_FIELDS = ['name', 'value', 'currency', 'pipeline', 'pipeline_stage'];

const CRM_FIELD_LABELS = computed(() => ({
  name: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CRM_FIELDS.NAME'),
  domain: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CRM_FIELDS.DOMAIN'),
  description: t(
    'COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CRM_FIELDS.DESCRIPTION'
  ),
}));

const CONTACT_FIELD_LABELS = computed(() => ({
  name: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CONTACT_FIELDS.NAME'),
  email: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CONTACT_FIELDS.EMAIL'),
  phone_number: t(
    'COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CONTACT_FIELDS.PHONE_NUMBER'
  ),
  identifier: t(
    'COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CONTACT_FIELDS.IDENTIFIER'
  ),
  city: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CONTACT_FIELDS.CITY'),
}));

const DEAL_FIELD_LABELS = computed(() => ({
  name: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.DEAL_FIELDS.NAME'),
  value: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.DEAL_FIELDS.VALUE'),
  currency: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.DEAL_FIELDS.CURRENCY'),
  pipeline: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.DEAL_FIELDS.PIPELINE'),
  pipeline_stage: t(
    'COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.DEAL_FIELDS.PIPELINE_STAGE'
  ),
}));

const contactOptions = computed(() => [
  ...CONTACT_FIELDS.map(field => ({
    value: `${CONTACT_PREFIX}${field}`,
    label: CONTACT_FIELD_LABELS.value[field],
  })),
  ...contactAttributes.value.map(attribute => ({
    value: `${CONTACT_PREFIX}${CUSTOM_ATTRIBUTE_PREFIX}${attribute.attributeKey}`,
    label: attribute.attributeDisplayName,
  })),
]);

const companyOptions = computed(() => [
  ...CRM_FIELDS.map(field => ({
    value: field,
    label: CRM_FIELD_LABELS.value[field],
  })),
  ...companyAttributes.value.map(attribute => ({
    value: `${CUSTOM_ATTRIBUTE_PREFIX}${attribute.attributeKey}`,
    label: attribute.attributeDisplayName,
  })),
]);

const dealOptions = computed(() => [
  ...DEAL_FIELDS.map(field => ({
    value: `${DEAL_PREFIX}${field}`,
    label: DEAL_FIELD_LABELS.value[field],
  })),
  ...dealAttributes.value.map(attribute => ({
    value: `${DEAL_PREFIX}${CUSTOM_ATTRIBUTE_PREFIX}${attribute.attributeKey}`,
    label: attribute.attributeDisplayName,
  })),
]);

const ENTITY_GROUPS = computed(() => [
  {
    label: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.ENTITY_GROUPS.CONTACT'),
    options: contactOptions.value,
  },
  {
    label: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.ENTITY_GROUPS.COMPANY'),
    options: companyOptions.value,
  },
  {
    label: t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.ENTITY_GROUPS.DEAL'),
    options: dealOptions.value,
  },
]);

const ALL_FIELD_VALUES = computed(() =>
  ENTITY_GROUPS.value.flatMap(group =>
    group.options.map(option => option.value)
  )
);

const { findIncompatibleSampleValue, getFormatHint } =
  useCustomAttributeValueValidation();

const attributeDefinitionByMappedValue = computed(() => {
  const definitions = {};
  contactAttributes.value.forEach(attribute => {
    definitions[
      `${CONTACT_PREFIX}${CUSTOM_ATTRIBUTE_PREFIX}${attribute.attributeKey}`
    ] = attribute;
  });
  companyAttributes.value.forEach(attribute => {
    definitions[`${CUSTOM_ATTRIBUTE_PREFIX}${attribute.attributeKey}`] =
      attribute;
  });
  dealAttributes.value.forEach(attribute => {
    definitions[
      `${DEAL_PREFIX}${CUSTOM_ATTRIBUTE_PREFIX}${attribute.attributeKey}`
    ] = attribute;
  });
  return definitions;
});

const incompatibleColumnWarnings = computed(() => {
  const warnings = [];
  Object.entries(fieldMapping.value).forEach(([column, mappedValue]) => {
    const definition = attributeDefinitionByMappedValue.value[mappedValue];
    if (!definition) return;

    const incompatibleValue = findIncompatibleSampleValue(
      csvPreviewRows.value,
      column,
      definition
    );
    if (incompatibleValue !== null) {
      warnings.push({
        column,
        definition,
        incompatibleValue,
        hint: getFormatHint(definition),
      });
    }
  });
  return warnings;
});

const hasFile = computed(() => selectedFile.value !== null);

const canImport = computed(() => {
  if (!hasFile.value) return false;
  if (csvHeaders.value.length === 0) return true;
  if (incompatibleColumnWarnings.value.length) return false;
  const mapped = Object.values(fieldMapping.value).filter(Boolean);
  const hasCompanyField = mapped.includes('name');
  const dealStageMapped = mapped.includes(`${DEAL_PREFIX}pipeline_stage`);
  const dealPipelineMapped = mapped.includes(`${DEAL_PREFIX}pipeline`);

  return hasCompanyField && (!dealStageMapped || dealPipelineMapped);
});

const processFileName = fileName => {
  const lastDot = fileName.lastIndexOf('.');
  const ext = fileName.slice(lastDot);
  const base = fileName.slice(0, lastDot);
  return base.length > 20 ? `${base.slice(0, 20)}...${ext}` : fileName;
};

const parsePreview = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      let text = e.target.result;
      text = text.replace(/^\ufeff/, '');
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length === 0) {
        resolve({ headers: [], rows: [] });
        return;
      }
      const parse = line =>
        line.split(',').map(v => v.replace(/^"|"$/g, '').trim());
      const headers = parse(lines[0]);
      const rows = lines
        .slice(1, 4)
        .map(parse)
        .map(cols =>
          Object.fromEntries(headers.map((h, i) => [h, cols[i] ?? '']))
        );
      resolve({ headers, rows });
    };
    reader.readAsText(file, 'UTF-8');
  });
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = async () => {
  const file = fileInput.value?.files[0];
  if (!file) return;
  selectedFile.value = file;
  selectedFileName.value = processFileName(file.name);

  const { headers, rows } = await parsePreview(file);
  csvHeaders.value = headers;
  csvPreviewRows.value = rows;

  const autoMap = {};
  headers.forEach(h => {
    const normalized = h.toLowerCase().replace(/ /g, '_');
    const match = ALL_FIELD_VALUES.value.find(
      value => value === normalized || value.split('.').pop() === normalized
    );
    autoMap[h] = match || '';
  });
  fieldMapping.value = autoMap;
};

const handleRemoveFile = () => {
  selectedFile.value = null;
  selectedFileName.value = '';
  csvHeaders.value = [];
  csvPreviewRows.value = [];
  fieldMapping.value = {};
  if (fileInput.value) fileInput.value.value = null;
};

const handleClose = () => {
  handleRemoveFile();
};

const uploadFile = () => {
  if (!selectedFile.value || !canImport.value) return;
  emit('import', {
    file: selectedFile.value,
    fieldMapping: fieldMapping.value,
  });
};

const onSuccess = () => {
  dialogRef.value?.close();
  handleClose();
};

defineExpose({ dialogRef, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.TITLE')"
    :confirm-button-label="t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.IMPORT')"
    :is-loading="isImporting"
    :disable-confirm-button="!canImport || isImporting"
    @confirm="uploadFile"
    @close="handleClose"
  >
    <!-- File picker -->
    <div class="flex items-center gap-2 mb-4">
      <label class="text-sm text-n-slate-12 whitespace-nowrap">
        {{ t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.LABEL') }}
      </label>
      <div class="flex items-center justify-between w-full gap-2">
        <span v-if="selectedFile" class="text-sm text-n-slate-12 truncate">
          {{ selectedFileName }}
        </span>
        <Button
          v-if="!selectedFile"
          :label="t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CHOOSE_FILE')"
          icon="i-lucide-upload"
          color="slate"
          variant="ghost"
          size="sm"
          class="!w-fit"
          @click="handleFileClick"
        />
        <div v-else class="flex items-center gap-1 shrink-0">
          <Button
            :label="t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.CHANGE')"
            color="slate"
            variant="ghost"
            size="sm"
            @click="handleFileClick"
          />
          <div class="w-px h-3 bg-n-strong" />
          <Button
            icon="i-lucide-trash"
            color="slate"
            variant="ghost"
            size="sm"
            @click="handleRemoveFile"
          />
        </div>
      </div>
    </div>

    <!-- Preview + mapping table -->
    <template v-if="hasFile && csvHeaders.length">
      <div class="overflow-auto rounded border border-n-weak mb-2">
        <table class="w-full text-xs text-n-slate-12 border-collapse">
          <thead class="bg-n-alpha-1">
            <tr>
              <th
                v-for="h in csvHeaders"
                :key="h"
                class="px-3 py-1.5 text-left font-medium whitespace-nowrap border-b border-n-weak min-w-[120px]"
              >
                {{ h }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in csvPreviewRows"
              :key="i"
              class="border-t border-n-weak"
            >
              <td
                v-for="h in csvHeaders"
                :key="h"
                class="px-3 py-1.5 whitespace-nowrap max-w-[160px] truncate text-n-slate-11"
              >
                {{ row[h] }}
              </td>
            </tr>
            <!-- Mapping selects row -->
            <tr class="border-t-2 border-n-strong">
              <td
                v-for="col in csvHeaders"
                :key="col"
                class="px-1.5 pt-2 pb-0.5 bg-n-alpha-2"
              >
                <select
                  v-model="fieldMapping[col]"
                  class="w-full text-xs rounded border border-n-weak bg-n-solid-3 px-1.5 py-1.5 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-8 cursor-pointer"
                >
                  <option value="">
                    {{
                      t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.IGNORE_COLUMN')
                    }}
                  </option>
                  <optgroup
                    v-for="group in ENTITY_GROUPS"
                    :key="group.label"
                    :label="group.label"
                  >
                    <option
                      v-for="option in group.options"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </optgroup>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!canImport" class="text-xs text-n-red-9 mb-3">
        {{ t('COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.MAPPING_REQUIRED') }}
      </p>
      <ul
        v-if="incompatibleColumnWarnings.length"
        class="text-xs text-n-red-9 mb-3 list-disc list-inside"
      >
        <li v-for="warning in incompatibleColumnWarnings" :key="warning.column">
          {{
            t(
              'COMPANIES_LAYOUT.HEADER.ACTIONS.IMPORT.INCOMPATIBLE_TYPE_WARNING',
              {
                column: warning.column,
                field: warning.definition.attributeDisplayName,
                value: warning.incompatibleValue,
                hint: warning.hint,
              }
            )
          }}
        </li>
      </ul>
    </template>
  </Dialog>

  <input
    ref="fileInput"
    type="file"
    accept="text/csv,.csv"
    class="hidden"
    @change="handleFileChange"
  />
</template>
